import type { MetaFunction, LoaderFunctionArgs } from 'react-router';
import { useLoaderData } from 'react-router';
import GmailLogo from '~/assets/gmail-glory.svg';
import { generateMeta } from '~/services/meta';
import { MicroLandingNew } from '~/components/ProductMicroLanding';
import ChromeDemo from '~/assets/chrome-demo.png';

import type { SitemapFunction } from 'remix-sitemap';

const AI_PROVIDERS = {
    claude: {
        name: 'Claude',
        title: 'Claude extension for Gmail',
        subTitle:
            "Integrate Anthropic's Claude AI into Gmail. Compose thoughtful emails, improve drafts, and get nuanced writing assistance directly in your inbox.",
        metaTitle: 'Claude Extension for Gmail',
        metaDescription:
            'Use Anthropic Claude AI in Gmail. Thoughtful email composition, draft improvement, and intelligent writing assistance.',
        features: [
            "✅ Anthropic's Claude AI for thoughtful email writing",
            '📧 Nuanced understanding of email context and tone',
            '✨ Improve drafts and compose better emails',
            '🕒 Save time with intelligent email assistance',
            '🔁 Works seamlessly in Gmail — integrated experience',
        ],
        feedback: {
            message:
                'Claude in Gmail helps me write the most thoughtful and professional emails. It understands the nuances of business communication perfectly.',
            name: 'Michael Thompson',
            role: 'Business Manager at CorporateSolutions',
        },
    },
    gemini: {
        name: 'Gemini',
        title: 'Gemini extension for Gmail',
        subTitle:
            "Get Google's powerful Gemini AI in Gmail. Compose better emails, reply professionally, and communicate effectively in any language.",
        metaTitle: 'Gemini Extension for Gmail',
        metaDescription:
            'Use Google Gemini AI in Gmail. Advanced email composition, multilingual support, and intelligent communication assistance.',
        features: [
            "✅ Google's advanced Gemini AI for email writing",
            '🌍 Multilingual email composition and replies',
            '✨ 1-click smart replies and email improvements',
            '🕒 Boost email productivity with AI assistance',
            '🔁 Seamless integration with Gmail interface',
        ],
        feedback: {
            message:
                'Gemini in Gmail has revolutionized our email communication. The AI creates perfect responses and helps us communicate globally.',
            name: 'Elena Rodriguez',
            role: 'International Communications Director',
        },
    },
    deepseek: {
        name: 'DeepSeek',
        title: 'DeepSeek extension for Gmail',
        subTitle:
            "Experience DeepSeek's advanced AI in Gmail. Compose intelligent emails with superior reasoning and context understanding.",
        metaTitle: 'DeepSeek Extension for Gmail',
        metaDescription:
            'Use DeepSeek AI in Gmail. Advanced email composition with superior reasoning and intelligent communication.',
        features: [
            "✅ DeepSeek's advanced AI for intelligent emails",
            '🧠 Superior reasoning for complex email communication',
            '✨ Context-aware email suggestions and improvements',
            '🕒 Accelerate email writing with smart assistance',
            '🔁 Work efficiently within Gmail interface',
        ],
        feedback: {
            message:
                'DeepSeek in Gmail understands complex business contexts and creates emails that perfectly convey our message and intent.',
            name: 'David Park',
            role: 'Strategic Communications Lead',
        },
    },
    perplexity: {
        name: 'Perplexity',
        title: 'Perplexity extension for Gmail',
        subTitle:
            "Bring Perplexity's research-powered AI to Gmail. Compose fact-based emails with accurate information and reliable sources.",
        metaTitle: 'Perplexity Extension for Gmail',
        metaDescription:
            'Use Perplexity AI in Gmail. Research-backed email composition with accurate, sourced information and fact-checking.',
        features: [
            '✅ Research-powered AI for accurate emails',
            '🔍 Include verified information in email content',
            '✨ Fact-checked email composition and replies',
            '🕒 Save research time with instant information',
            '🔁 Integrated research capabilities in Gmail',
        ],
        feedback: {
            message:
                'Perplexity in Gmail ensures our emails always contain accurate, well-sourced information. Perfect for professional communication.',
            name: 'Dr. Lisa Wang',
            role: 'Research Communications Director',
        },
    },
};

export const sitemap: SitemapFunction = () => {
    return Object.keys(AI_PROVIDERS).map((provider) => ({
        loc: `/plugin-for-gmail/${provider}`,
        lastmod: new Date().toISOString(),
        exclude: false,
    }));
};

export async function loader({ params }: LoaderFunctionArgs) {
    const integrationName = params.integrationName;

    if (
        !integrationName ||
        !AI_PROVIDERS[integrationName as keyof typeof AI_PROVIDERS]
    ) {
        throw new Response('Not Found', { status: 404 });
    }

    return {
        provider: AI_PROVIDERS[integrationName as keyof typeof AI_PROVIDERS],
    };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
    if (!data?.provider) {
        return [{ title: 'Not Found' }];
    }

    return generateMeta({
        title: data.provider.metaTitle,
        description: data.provider.metaDescription,
    });
};

export default function PluginGmailPage() {
    const { provider } = useLoaderData<typeof loader>();

    return (
        <MicroLandingNew
            hero={{
                imgUrl: GmailLogo,
                title: provider.title,
                subTitle: provider.subTitle,
                features: provider.features,
                samplePrompts: [
                    'reply-chat',
                    'fix-grammar',
                    'greeting',
                    'paraphrase',
                    'summarize',
                    'translate',
                    'apology',
                ],
                feedback: provider.feedback,
                install: [
                    {
                        label: 'Install Gmail Extension',
                        url: '/integrations/chrome',
                    },
                ],
            }}
            instruction={{
                title: 'How it works inside Gmail?',
                demoEl: (
                    <img src={ChromeDemo} alt='How it works inside Gmail?' />
                ),
                steps: [
                    <>
                        <b>Open Gmail:</b> Navigate to any email conversation or
                        compose window in Gmail
                    </>,
                    <>
                        <b>Use AI Perfect Assistant:</b> Click the AI Perfect
                        Assistant icon and select an email prompt or highlight
                        text to improve
                    </>,
                    <>
                        <b>Send or improve:</b> Use the AI-generated reply to
                        send instantly or improve your draft
                    </>,
                ],
            }}
        />
    );
}
